import { lazy, Suspense } from "react";
import { Link, useParams } from "react-router";
import { teachesEnglish, wordsFieldShown } from "@edu/shared";
import { useSession } from "../../lib/session.tsx";
import { useRawTeacherSubjects, useTeacherSubjects } from "../../lib/subjects.ts";
import { Crumbs } from "../shell/Crumbs.tsx";
import { StudentAccess } from "./StudentAccess.tsx";
import { StudentBrief } from "./StudentBrief.tsx";
import { StudentMaterials } from "./StudentMaterials.tsx";
import { StudentOccurrences } from "./StudentOccurrences.tsx";
import { StudentProfilePanel } from "./StudentProfilePanel.tsx";
import { StudentSkills } from "./StudentSkills.tsx";
import { useStudent } from "./use-student.ts";
import { Loading } from "../../lib/loading.tsx";

/**
 * Путь до карточки. Один на все три ветки экрана (отказ, ожидание, карточка):
 * крошки обязаны стоять на месте с первого кадра, иначе заголовок подпрыгивает
 * в момент, когда имя приезжает с сервера.
 */
const CRUMB_TRAIL = [{ label: "Ученики", to: "/students" }] as const;

/**
 * Карточки ученика ленивым чанком (10.1 `docs/flashcards-design.md`): поле слов
 * без занятия и сводка. Нужны только преподавателю английского.
 */
const StudentFlashcards = lazy(() =>
  import("../flashcards/StudentFlashcards.tsx").then((m) => ({ default: m.StudentFlashcards })),
);

/**
 * Карточка ученика: страница, и другого вида у неё нет.
 *
 * ⚠️ **Окно снято 14.09.2026** (решение владельца 13.09.2026). До этого дня один
 * адрес `/students/:id` рисовался ДВУМЯ экранами: щелчок из списка давал окно
 * 372 px, а прямой заход страницу, и различало их состояние перехода. Задумано
 * это было как два вида одного содержимого, но расходилась у них не обвязка, а
 * НАБОР БЛОКОВ: у окна не было ни крошек, ни раздела «Где дыры», ни учёта, ни
 * анкеты, зато был свой подвал со статусом ссылки, которого не было на странице.
 * Это четвёртая из пяти причин, по которым приложение читалось как несколько
 * программ (опись 13.09.2026, раздел 4).
 *
 * Цена решения названа честно: карточку открывают десятки раз в день, и уход со
 * списка стоит фильтров, прокрутки и расставленных галочек пакетного создания
 * уроков. Платим её сознательно, взамен получая один экран вместо двух.
 *
 * Порядок панелей перестроен 18.08.2026 и теперь идёт по ЧАСТОТЕ вопросов, а не
 * по важности решений. Раньше первым стоял «Доступ», куда заходят раз в жизни, а
 * занятия и домашка лежали под ним, хотя приходят сюда именно за ними.
 */
export function StudentPage() {
  const { studentId = "" } = useParams();
  const card = useStudent(studentId);
  /**
   * Переключатели учёта и зона живут в сессии, а не в карточке: это свойства
   * человека, одинаковые на всех его учеников, и таскать их в каждом ответе
   * `GET /v1/students/:id` значило бы платить за них по разу на ученика.
   */
  const { state } = useSession();
  const me = state.status === "signed" ? state.me : null;
  const tracking = me?.tracking ?? { attendance: false, payments: false };
  /*
    ⚠️ UTC это запасной вариант, а не умолчание: пояс подтверждает человек, и
    молча подставленный UTC сдвинул бы московскому преподавателю границу месяца
    на три часа. До подтверждения пояса кабинет и так показывает полосу «укажите
    часовой пояс», так что этот случай короткий.
  */
  const zone = me?.timeZone ?? "UTC";
  /** Панель «Где дыры» считает ОГЭ по математике: только тому, кто её ведёт. */
  const math = useTeacherSubjects().includes("math");
  /** Карточки: у преподавателя есть английский (8.3, правило `teachesEnglish`). */
  const english = teachesEnglish(useRawTeacherSubjects());

  if (card.err !== null && card.data === null) {
    return (
      <main className="home home--list">
        <header className="head">
          <Crumbs trail={CRUMB_TRAIL} here="…" />
        </header>
        <p className="err">{card.err}</p>
        <p>
          <Link className="btn" to="/students">
            К списку учеников
          </Link>
        </p>
      </main>
    );
  }

  if (card.data === null) {
    return (
      <main className="home home--list">
        <header className="head">
          <Crumbs trail={CRUMB_TRAIL} here="…" />
        </header>
        <Loading />
      </main>
    );
  }

  const s = card.data.student;
  /** Предмет пары из анкеты; без анкеты (старый API) как «не задан». */
  const pairSubject = card.data.profile?.subject ?? null;
  // Одно «сейчас» на всю отрисовку: строка, считающая его сама, к концу длинного
  // списка живёт уже в другой минуте, чем его начало
  const now = Date.now();

  return (
    <main className="home home--list">
      {/*
        ⚠️ Состояния ссылки в шапке нет, и это не потеря: оно стоит плашкой в
        заголовке панели «Доступ», где рядом с ним и сама ссылка, и отзыв. Один
        факт двумя средствами на одном экране это ровно то, ради чего заведено
        правило «факт не показывается дважды».
      */}
      <header className="head">
        <Crumbs trail={CRUMB_TRAIL} here={s.displayName} />
        <div className="head__line">
          <h1 className="head__h">{s.displayName}</h1>
          {/*
            ⚠️ **Главное действие карточки это «Задать домашнюю», и оно залитое.**
            Решение 14.09.2026, и оно ОТМЕНЯЕТ то, что стояло здесь раньше:
            «акцентной кнопки на этой странице нет, потому что на ней живёт
            необратимое, а акцент и опасность в одном месте не встречаются».

            Довод был верен по форме и неверен по адресу. Необратимое на этой
            странице это «Отозвать ссылку», и живёт оно в карточке «Доступ», в
            самом низу, за подтверждением в два нажатия. С шапкой экрана они не
            встречаются ни в одной строке, ни в одной карточке и ни в одном
            взгляде: «в одном месте» было прочитано как «на одной странице», а
            это разные вещи.

            Цена прежнего решения при этом была настоящей. Карточку открывают
            десятки раз в день, и чаще всего затем, чтобы задать работу; та же
            кнопка залита на экране домашки, то есть одна и та же кнопка шла
            двух весов на соседних экранах. Это вторая из пяти причин, по
            которым приложение читалось как несколько программ.

            Отвязанному работу не задать (сервер ответит 409
            `student_not_linked`), поэтому места главного действия у него нет, а
            причина названа словами в сводке ниже.
          */}
          {!s.unlinked && (
            <span className="head__act">
              <Link className="btn btn--primary" to={`/homework/new?studentId=${s.userId}`}>
                Задать домашку
              </Link>
            </span>
          )}
        </div>
      </header>

      {card.err !== null && <p className="err">{card.err}</p>}

      <StudentBrief data={card.data} now={now} tracking={tracking} />

      {/*
        ⚠️ «Где дыры» стоит ВЫШЕ занятий (1.6 docs/student-errors-design.md,
        развилка 12.5). Это меняет порядок, выстроенный 18.08.2026 по частоте
        вопросов, и меняет сознательно: ось продукта сменилась, и «где у него
        дыры» это теперь главный вопрос карточки. Решение обратимо перестановкой
        этой строки, и через месяц его стоит пересмотреть по тому, листают панель
        или пользуются ею.

        Своим запросом, а не полем карточки: он самый тяжёлый на экране и обязан
        падать отдельно, иначе панель уносит с собой занятия и ссылку.
      */}
      {math && <StudentSkills studentUserId={s.userId} />}

      {/*
        ⚠️ Занятия и учёт это ОДНА карточка с 26.09.2026 (решение владельца):
        список занятий, у которого колонки «Был» и «Оплата» появляются вместе с
        учётом. Раньше их было две, «Занятия» из уроков и «Учёт» из занятий
        расписания, и они говорили про одно и то же разными единицами.

        Отсутствие `occurrences` это старый API: тогда карточки нет вовсе, а
        не пустой список, который читался бы как «занятий не было».
      */}
      {card.data.occurrences !== undefined && (
        <StudentOccurrences
          // Другой ученик это другой список: догруженное и курсор не переезжают
          key={s.userId}
          data={card.data.occurrences}
          payment={card.data.payment}
          tracking={tracking}
          zone={zone}
          studentUserId={s.userId}
          now={now}
          busy={card.busy}
          onMark={card.setMark}
          onSettle={card.settle}
          onMonth={card.setMonth}
        />
      )}

      {/*
        Карточки (docs/flashcards-design.md, 8.2 и 9.5): поле слов без занятия и
        сводка по колодам. Поле только у пары с английским или без предмета, и
        правило одно на сетку и этот экран (`wordsFieldShown`); «другое» в
        анкете это не английский. Отвязанной паре слов не добавить (409).
      */}
      {english && me !== null && (
        <Suspense fallback={null}>
          <StudentFlashcards
            key={s.userId}
            userId={me.userId}
            studentUserId={s.userId}
            wordsField={!s.unlinked && pairSubject !== "other" && wordsFieldShown(pairSubject)}
            zone={zone}
          />
        </Suspense>
      )}

      {/*
        Ссылки и файлы (docs/material-receipt-design.md, часть А) стоят сразу
        после занятий и до анкеты: отправляют их между занятиями, то есть чаще,
        чем правят анкету, и реже, чем смотрят занятия. Тот же порядок «по
        частоте вопросов», что у всей карточки. Своим запросом: список падает
        отдельно и карточку с собой не уносит.
      */}
      <StudentMaterials
        key={s.userId}
        studentUserId={s.userId}
        studentName={s.displayName}
        unlinked={s.unlinked}
        now={now}
      />

      {/*
        Анкета стоит ПОСЛЕ занятий, а не до: сюда заходят при заведении ученика
        и потом изредка, а занятия смотрят каждый день. Тот же порядок «по
        частоте вопросов, а не по важности решений», по которому 18.08.2026
        перестроили всю карточку.
      */}
      {card.data.profile !== undefined && (
        <StudentProfilePanel
          profile={card.data.profile}
          busy={card.busy}
          paymentsTracked={tracking.payments}
          onSave={card.saveProfile}
          onAddContact={card.addContact}
          onEditContact={card.editContact}
          onRemoveContact={card.removeContact}
        />
      )}

      <StudentAccess card={card} student={s} />
    </main>
  );
}
